import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import type {
  UVCoordinates,
  HeatmapGenerationConfig,
  HeatmapColorScheme,
  GazePoint,
  FixationDetectionConfig,
} from "../types/gazeHeatmapTypes";
import { directionToEquirectangularUV } from "../utils/coordinateConversion";
import {
  generateHeatmap,
  samplePoints,
  getAdaptiveHeatmapConfig,
} from "../utils/heatmapGeneration";

// Maximum points before switching to Web Worker
const WORKER_THRESHOLD = 1000;
// Maximum points to process (sampling applied beyond this)
const MAX_POINTS = 10000;

interface UseHeatmapGenerationParams {
  points: GazePoint[];
  config?: Partial<HeatmapGenerationConfig>;
  enabled?: boolean;
  useWorker?: boolean;
  /** Scene rotation in degrees (Y axis) to offset the coordinate origin */
  sceneRotationY?: number;
}

interface UseHeatmapGenerationResult {
  imageData: ImageData | null;
  isGenerating: boolean;
  progress: number;
  error: string | null;
  maxDensity: number;
  pointCount: number;
  processingTime: number;
  regenerate: () => void;
}

/**
 * Hook to generate heatmap from gaze points
 * Uses Web Worker for large datasets to prevent UI blocking
 */
export function useHeatmapGeneration({
  points,
  config = {},
  enabled = true,
  useWorker = true,
  sceneRotationY = 0,
}: UseHeatmapGenerationParams): UseHeatmapGenerationResult {
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [maxDensity, setMaxDensity] = useState(0);
  const [pointCount, setPointCount] = useState(0);
  const [processingTime, setProcessingTime] = useState(0);

  const workerRef = useRef<Worker | null>(null);
  const generationIdRef = useRef(0);

  // Convert gaze points to UV coordinates (with scene rotation offset)
  const convertToUV = useCallback(
    (gazePoints: GazePoint[]): UVCoordinates[] => {
      return gazePoints.map((p) => directionToEquirectangularUV(p.direction, sceneRotationY));
    },
    [sceneRotationY],
  );

  // Get adaptive config based on device capabilities (memoized)
  const adaptiveConfig = useMemo(() => getAdaptiveHeatmapConfig(config), [config]);

  // Generate heatmap (main function)
  const generate = useCallback(() => {
    if (!enabled || points.length === 0) {
      setImageData(null);
      setIsGenerating(false);
      return;
    }

    // Increment generation ID to handle race conditions
    generationIdRef.current += 1;
    const currentGenerationId = generationIdRef.current;

    setIsGenerating(true);
    setProgress(0);
    setError(null);

    // Sample points if too many
    const processedPoints = points.length > MAX_POINTS ? samplePoints(points, MAX_POINTS) : points;

    // Convert to UV coordinates
    const uvPoints = convertToUV(processedPoints);

    // Use adaptive config (already merged with user config)
    const fullConfig = adaptiveConfig;

    // Decide whether to use Web Worker
    const shouldUseWorker = useWorker && uvPoints.length > WORKER_THRESHOLD;

    if (shouldUseWorker) {
      // Use Web Worker for large datasets
      try {
        // Create worker if not exists
        if (!workerRef.current) {
          workerRef.current = new Worker(new URL("../utils/heatmapWorker.ts", import.meta.url), {
            type: "module",
          });
        }

        const worker = workerRef.current;

        worker.onmessage = (e) => {
          // Check if this is still the current generation
          if (generationIdRef.current !== currentGenerationId) return;

          const { type, result, error: workerError, progress: workerProgress } = e.data;

          if (type === "progress") {
            setProgress(workerProgress);
          } else if (type === "result") {
            // Reconstruct ImageData from transferred array
            const reconstructedImageData = new ImageData(
              new Uint8ClampedArray(result.imageDataArray),
              result.width,
              result.height,
            );

            setImageData(reconstructedImageData);
            setMaxDensity(result.maxDensity);
            setPointCount(result.pointCount);
            setProcessingTime(result.processingTime);
            setIsGenerating(false);
            setProgress(100);
          } else if (type === "error") {
            setError(workerError);
            setIsGenerating(false);
          }
        };

        worker.onerror = (e) => {
          if (generationIdRef.current !== currentGenerationId) return;
          setError(e.message || "Worker error");
          setIsGenerating(false);
        };

        // Send message to worker
        worker.postMessage({
          type: "generate",
          points: uvPoints,
          config: fullConfig,
        });
      } catch (err) {
        // Fallback to main thread if worker fails
        console.warn("Web Worker failed, falling back to main thread:", err);
        generateOnMainThread(uvPoints, fullConfig, currentGenerationId);
      }
    } else {
      // Use main thread for small datasets
      generateOnMainThread(uvPoints, fullConfig, currentGenerationId);
    }
  }, [points, adaptiveConfig, enabled, useWorker, convertToUV]);

  // Generate on main thread
  const generateOnMainThread = useCallback(
    (uvPoints: UVCoordinates[], fullConfig: HeatmapGenerationConfig, generationId: number) => {
      try {
        // Use requestAnimationFrame to allow UI updates
        requestAnimationFrame(() => {
          if (generationIdRef.current !== generationId) return;

          const result = generateHeatmap(uvPoints, fullConfig);

          if (generationIdRef.current !== generationId) return;

          setImageData(result.imageData);
          setMaxDensity(result.maxDensity);
          setPointCount(result.pointCount);
          setProcessingTime(result.processingTime);
          setIsGenerating(false);
          setProgress(100);
        });
      } catch (err) {
        if (generationIdRef.current !== generationId) return;
        setError(err instanceof Error ? err.message : "Generation failed");
        setIsGenerating(false);
      }
    },
    [],
  );

  // Trigger generation when points or config change
  useEffect(() => {
    generate();
  }, [generate]);

  // Cleanup worker on unmount
  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  return {
    imageData,
    isGenerating,
    progress,
    error,
    maxDensity,
    pointCount,
    processingTime,
    regenerate: generate,
  };
}

/**
 * Hook to manage heatmap color scheme with local storage persistence
 */
export function useHeatmapColorScheme(
  defaultScheme: HeatmapColorScheme = "thermal",
): [HeatmapColorScheme, (scheme: HeatmapColorScheme) => void] {
  const [colorScheme, setColorScheme] = useState<HeatmapColorScheme>(() => {
    try {
      const stored = localStorage.getItem("heatmapColorScheme");
      if (stored && ["green", "thermal", "viridis"].includes(stored)) {
        return stored as HeatmapColorScheme;
      }
    } catch {
      // Ignore localStorage errors
    }
    return defaultScheme;
  });

  const setAndPersist = useCallback((scheme: HeatmapColorScheme) => {
    setColorScheme(scheme);
    try {
      localStorage.setItem("heatmapColorScheme", scheme);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return [colorScheme, setAndPersist];
}

/**
 * Hook to manage heatmap opacity with local storage persistence
 */
export function useHeatmapOpacity(defaultOpacity = 1.0): [number, (opacity: number) => void] {
  const [opacity, setOpacity] = useState<number>(() => {
    try {
      const stored = localStorage.getItem("heatmapOpacity");
      if (stored) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 1) {
          return parsed;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    return defaultOpacity;
  });

  const setAndPersist = useCallback((newOpacity: number) => {
    const clamped = Math.max(0, Math.min(1, newOpacity));
    setOpacity(clamped);
    try {
      localStorage.setItem("heatmapOpacity", String(clamped));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return [opacity, setAndPersist];
}

/** Display mode for session detail: detailed (with scenes), summary (without scenes), or graph */
export type SceneDisplayMode = "detailed" | "summary" | "graph";

/**
 * Hook to manage scene display mode with local storage persistence
 * Default: "summary" (collapsed view without scene hierarchy)
 */
export function useSceneDisplayMode(
  defaultMode: SceneDisplayMode = "summary",
): [SceneDisplayMode, (mode: SceneDisplayMode) => void] {
  const [displayMode, setDisplayMode] = useState<SceneDisplayMode>(() => {
    try {
      const stored = localStorage.getItem("sceneDisplayMode");
      if (stored && (stored === "detailed" || stored === "summary" || stored === "graph")) {
        return stored as SceneDisplayMode;
      }
    } catch {
      // Ignore localStorage errors
    }
    return defaultMode;
  });

  const setAndPersist = useCallback((mode: SceneDisplayMode) => {
    setDisplayMode(mode);
    try {
      localStorage.setItem("sceneDisplayMode", mode);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return [displayMode, setAndPersist];
}

/**
 * Hook to manage scanpath configuration with localStorage persistence
 */
export function useScanpathConfig(
  defaultConfig: FixationDetectionConfig = { displayThreshold: 0, mergeRadius: 1 },
): [FixationDetectionConfig, (config: FixationDetectionConfig) => void] {
  const [config, setConfig] = useState<FixationDetectionConfig>(() => {
    try {
      const stored = localStorage.getItem("scanpathConfig");
      if (stored) {
        const parsed = JSON.parse(stored) as FixationDetectionConfig;
        return {
          displayThreshold: parsed.displayThreshold ?? defaultConfig.displayThreshold,
          mergeRadius: parsed.mergeRadius ?? defaultConfig.mergeRadius,
        };
      }
    } catch {
      // Ignore localStorage errors
    }
    return defaultConfig;
  });

  const setAndPersist = useCallback((newConfig: FixationDetectionConfig) => {
    setConfig(newConfig);
    try {
      localStorage.setItem("scanpathConfig", JSON.stringify(newConfig));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  return [config, setAndPersist];
}
